import { useState, useEffect } from 'react';
import { FileText, CheckCircle2, FolderOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { moduleService, Module } from '@/services/moduleService';
import { isElectronEnvironment } from '@/hooks/useElectronFileSystem';
import { useClientDocumentsConfig } from '@/hooks/useClientDocumentsConfig';
import { normalizeDocumentPath } from '@/utils/normalizeDocumentPath';
import { joinUncDocumentsPath } from '@/utils/joinUncPath';
import { apiClient } from '@/utils/api';
import { toast } from 'sonner';

const MODULES_SUBFOLDER = 'ModuliStudioGori';

interface ActivityFormDialogProps {
  open: boolean;
  onClose: () => void;
  activityTitle: string;
  activityTypeCode: string;
  practiceId?: string;
  practiceDocumentPath?: string;
  subfolder?: string;
  moduleId?: string | null;
}

export default function ActivityFormDialog({
  open,
  onClose,
  activityTitle,
  activityTypeCode,
  practiceId,
  practiceDocumentPath: practiceDocumentPathProp,
  subfolder,
  moduleId,
}: ActivityFormDialogProps) {
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState(true);
  const [copiedFile, setCopiedFile] = useState<string | null>(null);
  const [resolvedDocPath, setResolvedDocPath] = useState<string | undefined>(undefined);
  const inElectron = isElectronEnvironment();
  const { documentsNasRoot } = useClientDocumentsConfig();

  const practiceDocumentPath = practiceDocumentPathProp || resolvedDocPath;

  const modulesNasPath = (() => {
    const root = normalizeDocumentPath(documentsNasRoot || '');
    if (!root) return '';
    return joinUncDocumentsPath(root, MODULES_SUBFOLDER);
  })();

  useEffect(() => {
    if (open && !practiceDocumentPathProp && practiceId) {
      apiClient.get(`/practices/${practiceId}`)
        .then(res => {
          const docPath = res.data?.data?.practice?.documentPath || res.data?.data?.documentPath || res.data?.documentPath;
          if (docPath) setResolvedDocPath(docPath);
        })
        .catch(() => {});
    }
    if (!open) {
      setResolvedDocPath(undefined);
    }
  }, [open, practiceId, practiceDocumentPathProp]);

  useEffect(() => {
    if (open && activityTypeCode) {
      setLoading(true);
      setCopiedFile(null);

      if (moduleId) {
        moduleService.getModules()
          .then(all => setModules(all.filter(m => m.id === moduleId)))
          .catch(() => toast.error('Errore nel caricamento del modulo'))
          .finally(() => setLoading(false));
      } else {
        moduleService.getModulesByActivityType(activityTypeCode)
          .then(setModules)
          .catch(() => toast.error('Errore nel caricamento dei moduli'))
          .finally(() => setLoading(false));
      }
    }
  }, [open, activityTypeCode, moduleId]);

  const handleCopyAndOpen = async (mod: Module) => {
    if (!inElectron || !window.electronFS) {
      toast.error('Funzione disponibile solo dal client desktop');
      return;
    }

    if (!mod.nasRelativePath) {
      toast.error('Modulo non basato su NAS');
      return;
    }

    if (!practiceDocumentPath) {
      toast.error('Nessuna cartella documenti associata a questa pratica');
      return;
    }

    try {
      const sourcePath = window.electronFS.joinPath(modulesNasPath, mod.nasRelativePath);
      if (!window.electronFS.pathExists(sourcePath)) {
        toast.error(`File template non trovato: ${mod.nasRelativePath}`);
        return;
      }

      const practicePath = normalizeDocumentPath(practiceDocumentPath) || practiceDocumentPath;

      let targetDir = practicePath;
      if (subfolder) {
        targetDir = window.electronFS.joinPath(practicePath, subfolder);
        if (!window.electronFS.directoryExists(targetDir)) {
          window.electronFS.createDirectory(targetDir);
        }
      }

      const ext = window.electronFS.extname(mod.nasRelativePath);
      const baseName = mod.nasRelativePath.replace(new RegExp(`${ext.replace('.', '\\.')}$`), '');
      const datePrefix = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const destFileName = `${datePrefix}_${baseName}${ext}`;
      const destPath = window.electronFS.joinPath(targetDir, destFileName);

      const content = window.electronFS.readFile(sourcePath);
      window.electronFS.writeFile(destPath, content);

      setCopiedFile(destFileName);
      toast.success(`Modulo copiato: ${destFileName}`);

      await window.electronFS.openPath(destPath);
    } catch (err: any) {
      toast.error(err.message || 'Errore nella copia del modulo');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose(); setCopiedFile(null); } }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Moduli — {activityTitle}</DialogTitle>
        </DialogHeader>

        {practiceDocumentPath && (
          <p className="text-sm text-gray-500">
            Cartella pratica: <span className="font-mono text-xs">{practiceDocumentPath}</span>
          </p>
        )}

        {loading ? (
          <div className="text-center py-4 text-gray-500">Caricamento moduli...</div>
        ) : modules.length === 0 ? (
          <div className="text-center py-4 text-gray-500">
            <FileText className="h-8 w-8 mx-auto mb-2 text-gray-400" />
            Nessun modulo associato a questo tipo di attività
          </div>
        ) : inElectron ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Clicca per copiare il template nella cartella della pratica e aprirlo:
            </p>
            {modules.map((mod) => (
              <div key={mod.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <p className="font-medium text-sm">{mod.name}</p>
                  <p className="text-xs text-gray-500">{mod.nasRelativePath || mod.originalName}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCopyAndOpen(mod)}
                  disabled={!mod.nasRelativePath}
                >
                  <FolderOpen className="h-4 w-4 mr-1" />
                  Compila
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-4 text-amber-600 bg-amber-50 border border-amber-200 rounded p-4">
            La compilazione dei moduli richiede il client desktop Studio Gori.
          </div>
        )}

        {copiedFile && (
          <div className="flex items-center gap-2 text-green-700 text-sm">
            <CheckCircle2 className="h-4 w-4" />
            File "{copiedFile}" copiato e aperto
          </div>
        )}

        <div className="flex justify-end">
          <Button variant="outline" onClick={() => { onClose(); setCopiedFile(null); }}>
            Chiudi
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
